import {inject, Injectable} from "@angular/core";
import {HttpClient, HttpHeaders, HttpParams} from "@angular/common/http";
import {UrlService} from "../../core/services/url.service";
import {ErrorHandlingService} from "../../core/services/error.handling.service";
import {BehaviorSubject, Observable, tap} from "rxjs";
import {
    RecallResponseDTO,
    RecallResponseDTOPage,
    RecallUpdateDTO,
    RecallMsg,
    RecallTemplate,
    RecallStatus, CustomRecall
} from "./recall.types";
import {UrlEnum} from "../../core/types/url-enum";
import {ObservableErrHandler} from "../../core/types/err.handling.types";
import {DateTimeService} from "../../core/services/date.time.service";

@Injectable({ providedIn: 'root' })
export class RecallService {
    private readonly _httpClient = inject(HttpClient);
    private readonly _urlService: UrlService = inject(UrlService);
    private readonly _errHandlService: ErrorHandlingService = inject(ErrorHandlingService);
    private readonly _dts: DateTimeService = inject(DateTimeService);
    private readonly _busy: BehaviorSubject<boolean> = new BehaviorSubject(false);
    private readonly _recallsPage: BehaviorSubject<RecallResponseDTOPage> = new BehaviorSubject(null);
    private readonly _attentionNeeded: BehaviorSubject<number> = new BehaviorSubject(0);

    get attentionNeeded$() {
        return this._attentionNeeded.asObservable();
    }

    get busy$() {
        return this._busy.asObservable();
    }

    get recallsPage$() {
        return this._recallsPage.asObservable();
    }

    public getRecall(id: string): Observable<RecallResponseDTO> {
        this._busy.next(true);
        const obs: Observable<RecallResponseDTO> = this._httpClient.get<RecallResponseDTO>(
            this._urlService.getUrl(UrlEnum.private_recall).replace("{id}", id)
        );

        const errWrapper: ObservableErrHandler = {
            observable: obs,
            err_title: "Error"
        };
        this._errHandlService.watchThis(errWrapper);
        return errWrapper.observable.pipe(tap({
            next: () => this._busy.next(false),
            error: () => this._busy.next(false),
            complete: () => this._busy.next(false)
        }));
    }

    public updateRecall(recall: RecallUpdateDTO): Observable<RecallResponseDTO> {
        this._busy.next(true);
        const headers = new HttpHeaders().set('Content-Type', 'application/json; charset=utf-8');
        const obs: Observable<RecallResponseDTO> = this._httpClient.put<RecallResponseDTO>(
            this._urlService.getUrl(UrlEnum.private_recall).replace("{id}", recall.id),
            JSON.stringify(recall),
            {headers: headers}
        );

        const errWrapper: ObservableErrHandler = {
            observable: obs,
            err_title: "Error",
            success_title: "S-a facut",
            success_msg: "Recall-ul a fost actualizat cu succes."
        };
        this._errHandlService.watchThis(errWrapper);
        return errWrapper.observable.pipe(tap({
            next: () => {
                this._busy.next(false)
                // check recall in current page and update it
                const index = this._recallsPage.value.content.findIndex(r => r.id == recall.id);
                if(index >= 0){
                    this._recallsPage.value.content[index].recallDate = recall.recallDate;
                    this._recallsPage.value.content[index].status = recall.status;
                    this._recallsPage.value.content[index].userResponse = recall.userResponse;
                    this._recallsPage.value.content[index].name = recall.name;
                    this._recallsPage.value.content[index].details = recall.details;
                    this.updateAttentionNeededCount(this._recallsPage.value.content);
                    this._recallsPage.next(this._recallsPage.value);
                }
            },
            error: () => this._busy.next(false),
            complete: () => this._busy.next(false)
        }));
    }

    public getRecallsByDate(from: string, to: string, page: number, size: number, sortBy: string): Observable<RecallResponseDTOPage> {
        this._busy.next(true);
        let params = new HttpParams()
            .set('from', from)
            .set('to', to)
            .set('page', page.toString())
            .set('size', size.toString())
            .set('sort', sortBy);

        const obs: Observable<RecallResponseDTOPage> = this._httpClient.get<RecallResponseDTOPage>(
            this._urlService.getUrl(UrlEnum.private_recalls),
            {params: params}
        );

        const errWrapper: ObservableErrHandler = {
            observable: obs,
            err_title: "Error"
        };
        this._errHandlService.watchThis(errWrapper);
        return errWrapper.observable.pipe(tap({
            next: (page) => {
                this._recallsPage.next(page);
                this._busy.next(false)
            },
            error: () => this._busy.next(false),
            complete: () => this._busy.next(false)
        }));
    }

    public getRecallsByStatus(status:RecallStatus[], page: number, size: number, sortBy: string): Observable<RecallResponseDTOPage> {
        this._busy.next(true);
        let params = new HttpParams()
            .set('page', page.toString())
            .set('size', size.toString())
            .set('sort', sortBy);

        status.forEach(s => {
            params = params.append('status', s);
        });

        const obs: Observable<RecallResponseDTOPage> = this._httpClient.get<RecallResponseDTOPage>(
            this._urlService.getUrl(UrlEnum.private_recalls_status_based),
            {params: params}
        );

        const errWrapper: ObservableErrHandler = {
            observable: obs,
            err_title: "Error"
        };
        this._errHandlService.watchThis(errWrapper);
        return errWrapper.observable.pipe(tap({
            next: (page) => {
                this._recallsPage.next(page);
                this.updateAttentionNeededCount(page.content);
                this._busy.next(false)
            },
            error: () => this._busy.next(false),
            complete: () => this._busy.next(false)
        }));
    }

    private doesRecallNeedAttention(recall: RecallResponseDTO){
        return (recall.status == RecallStatus.IN_PROGRESS || recall.status == RecallStatus.SCHEDULED)  && this.isMature(recall);
    }

    public getRecallsByUser(userID): Observable<RecallResponseDTO[]> {
        this._busy.next(true);


        const obs: Observable<RecallResponseDTO[]> = this._httpClient.get<RecallResponseDTO[]>(
            this._urlService.getUrl(UrlEnum.private_recalls_by_user)
                .replace("{userId}", userID)
        );

        const errWrapper: ObservableErrHandler = {
            observable: obs,
            err_title: "Error"
        };
        this._errHandlService.watchThis(errWrapper);
        return errWrapper.observable.pipe(tap({
            next: (content) => {
                if(this._recallsPage.value){
                    this._recallsPage.value.content = [].concat(content);
                    this._recallsPage.next(this._recallsPage.value);
                }else{
                    const page: RecallResponseDTOPage = new RecallResponseDTOPage();
                    page.content = [].concat(content);
                    this._recallsPage.next(page);
                }

                this._busy.next(false);
            },
            error: () => this._busy.next(false),
            complete: () => this._busy.next(false)
        }));
    }

    public getNotificationTemplateMsg(recallID:string, type:RecallTemplate):Observable<RecallMsg>{
        this._busy.next(true);
        let notificationType:string = "SMS";
        switch (type){
            case RecallTemplate.GENERIC_SMS_TEMPLATE: notificationType = "SMS"; break;
            case RecallTemplate.GENERIC_EMAIL_TEMPLATE: notificationType = "EMAIL"; break;
        }

        const obs: Observable<RecallMsg> = this._httpClient.get<RecallMsg>(
            this._urlService.getUrl(UrlEnum.private_recall_template_get)
                .replace("{id}", recallID)
                .replace("{notificationType}", notificationType),
        );

        const errWrapper: ObservableErrHandler = {
            observable: obs,
            err_title: "Error"
        };
        this._errHandlService.watchThis(errWrapper);
        return errWrapper.observable.pipe(tap({
            next: () => {
                this._busy.next(false)
            },
            error: () => this._busy.next(false),
            complete: () => this._busy.next(false)
        }));
    }

    public sendNotificationMessage(recallID:string, msg:RecallMsg):Observable<string>{
        this._busy.next(true);
        const headers = new HttpHeaders().set('Content-Type', 'application/json; charset=utf-8');
        const obs: Observable<any> = this._httpClient.post<any>(
            this._urlService.getUrl(UrlEnum.private_recall_template).replace("{id}", recallID),
            JSON.stringify(msg),  {headers: headers}
        );

        const errWrapper: ObservableErrHandler = {
            observable: obs,
            err_title: "Error",
            success_title: "S-a facut",
            success_msg: "Mesajul a fost trimis cu succes"
        };
        this._errHandlService.watchThis(errWrapper);
        return errWrapper.observable.pipe(tap({
            next: () => {
                if(this._recallsPage.value){
                    const recall = this._recallsPage.value.content.find(r => r.id == recallID);
                    recall.status = RecallStatus.IN_PROGRESS;
                }
                this._recallsPage.next(this._recallsPage.value);
                this._busy.next(false);
            },
            error: () => this._busy.next(false),
            complete: () => this._busy.next(false)
        }));
    }

    public createCustomRecall(recall:CustomRecall): Observable<RecallResponseDTO> {
        this._busy.next(true);
        const headers = new HttpHeaders().set('Content-Type', 'application/json; charset=utf-8');
        const obs: Observable<RecallResponseDTO> = this._httpClient.post<RecallResponseDTO>(
            this._urlService.getUrl(UrlEnum.private_recalls),
            JSON.stringify(recall),
            {headers: headers}
        );

        const errWrapper: ObservableErrHandler = {
            observable: obs,
            err_title: "Error",
            success_title: "S-a facut",
            success_msg: "Recall-ul a fost programat cu succes."
        };
        this._errHandlService.watchThis(errWrapper);
        return errWrapper.observable.pipe(tap({
            next: () => this._busy.next(false),
            error: () => this._busy.next(false),
            complete: () => this._busy.next(false)
        }));
    }

    public isMature(recall:RecallResponseDTO):boolean{
        const recallDate = this._dts.getDateAndTimeFromBEInDateObject(recall.recallDate)
        return this._dts.dateInPast(recallDate)
    }

    private updateAttentionNeededCount(recalls: RecallResponseDTO[]) {
        const attentionNeededCount =recalls.filter( (recall: RecallResponseDTO) => this.doesRecallNeedAttention(recall)).length;
        this._attentionNeeded.next(attentionNeededCount);
    }
}
